import { ApiQueryName } from "@/models/api"
import type { CurrencyCode } from "@/models/currencyCode"
import type { Organization } from "@/models/organization"
import type { IntervalListPublicApiQueryOptions } from "@/services/core-api/types"
import { createQueryKey } from "@/services/utils/createQueryKey"
import { toHttpError } from "@/utils/api"

import { getIntervals } from "./get-intervals"
import type { IntervalItem } from "./interval.types"

// Same 10_000 cap as Rails IntervalsListQuery::MAX_LIMIT. Below Core API's
// 25_000 ceiling so one page can finish inside Rack::Timeout (15s).
const INTERVALS_PAGE_SIZE = 10_000
export const INTERVALS_MAX_PAGES = 100

export interface IntervalsQueryData {
  items: IntervalItem[]
}

interface FetchIntervalsQueryDataParams {
  apiQueryOptions: IntervalListPublicApiQueryOptions
  maxPages?: number
  organizationId: Organization["id"]
  pageThrough?: boolean
}

interface BuildIntervalsQueryParams extends FetchIntervalsQueryDataParams {
  currency: CurrencyCode
  enabled?: boolean
}

const fetchAllIntervalItems = async ({
  apiQueryOptions,
  maxPages = INTERVALS_MAX_PAGES,
  organizationId,
}: FetchIntervalsQueryDataParams): Promise<IntervalItem[]> => {
  const items: IntervalItem[] = []
  let offset = 0
  const limit = apiQueryOptions.limit ?? INTERVALS_PAGE_SIZE

  for (let page = 0; page < maxPages; page += 1) {
    const response = await getIntervals({
      apiQueryOptions: {
        ...apiQueryOptions,
        limit,
        offset,
      },
      organizationId,
    })
    items.push(...response.items)

    if (response.items.length === 0) {
      return items
    }

    offset += response.items.length
    const total = response.total
    if (typeof total === "number" && offset >= total) {
      return items
    }
  }

  throw toHttpError(new Error("Interval results exceeded the page cap"))
}

const fetchIntervalsQueryData = async ({
  apiQueryOptions,
  maxPages = INTERVALS_MAX_PAGES,
  organizationId,
  pageThrough = true,
}: FetchIntervalsQueryDataParams): Promise<IntervalsQueryData> => {
  if (!pageThrough) {
    const page = await getIntervals({
      apiQueryOptions: {
        ...apiQueryOptions,
        limit: apiQueryOptions.limit ?? INTERVALS_PAGE_SIZE,
        offset: 0,
      },
      organizationId,
    })
    return { items: page.items }
  }

  return {
    items: await fetchAllIntervalItems({
      apiQueryOptions,
      maxPages,
      organizationId,
    }),
  }
}

export const buildIntervalsQuery = ({
  apiQueryOptions,
  currency,
  enabled = true,
  maxPages = INTERVALS_MAX_PAGES,
  organizationId,
  pageThrough = true,
}: BuildIntervalsQueryParams) => ({
  enabled:
    Boolean(organizationId) && Boolean(apiQueryOptions.site_id) && enabled,
  queryFn: async (): Promise<IntervalsQueryData> =>
    fetchIntervalsQueryData({
      apiQueryOptions,
      maxPages,
      organizationId,
      pageThrough,
    }),
  queryKey: createQueryKey(ApiQueryName.Intervals, "getMany", null, {
    apiQueryOptions,
    currency,
    maxPages,
    organizationId,
    pageThrough,
  }),
})
